import { AxiosError } from "axios";

import { request } from "../../apiService";
import {
    LearnerRow,
    LearnersResponse,
    Promotion,
    PromotionRepeatRow,
    PromotionRepeatsResponse,
    PromotionInput,
    PromotionsResponse,
} from "../../../types/api/admin/promotions";

export const getPromotions = async (params: {
    page?: number;
    limit?: number;
    search?: string;
} = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return await request<PromotionsResponse>(
        `/v3/admin/promotions${suffix}`
    );
};

/** Liste complète pour selects / filtres. */
export const getPromotionOptions = async () => {
    return await request<Promotion[]>(`/v3/admin/promotions/options`);
};

export const createPromotion = async (input: PromotionInput) => {
    return await request<Promotion, PromotionInput>(`/v3/admin/promotions`, {
        method: "POST",
        input,
    });
};

export const updatePromotion = async (
    id: string,
    input: Partial<PromotionInput>
) => {
    return await request<Promotion, Partial<PromotionInput>>(
        `/v3/admin/promotions/${id}`,
        {
            method: "PATCH",
            input,
        }
    );
};

export const deletePromotion = async (id: string) => {
    return await request<void>(`/v3/admin/promotions/${id}`, {
        method: "DELETE",
    });
};

export const getLearners = async (params: {
    page?: number;
    limit?: number;
    search?: string;
    promotionId?: string;
    anomaly?: "only" | "none";
}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    if (params.promotionId) qs.set("promotionId", params.promotionId);
    if (params.anomaly) qs.set("anomaly", params.anomaly);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return await request<LearnersResponse>(
        `/v3/admin/promotions/learners${suffix}`
    );
};

export type AssignPromotionResult = {
    updated: number;
    promotedToJunior: number;
    repeatsRecorded: number;
};

export const readRepeatConfirmationUserIds = (error: unknown): string[] | null => {
    if (!(error instanceof AxiosError) || error.response?.status !== 409) {
        return null;
    }
    const data = error.response.data as { code?: string; userIds?: string[] };
    if (data?.code !== "REPEAT_CONFIRMATION_REQUIRED") return null;
    return data.userIds ?? [];
};

export const assignUsersToPromotion = async (
    promotionId: string,
    userIds: string[],
    countAsRepeat?: boolean
) => {
    return await request<
        AssignPromotionResult,
        { userIds: string[]; countAsRepeat?: boolean }
    >(`/v3/admin/promotions/${promotionId}/assign`, {
        method: "POST",
        input:
            countAsRepeat === undefined
                ? { userIds }
                : { userIds, countAsRepeat },
    });
};

export const getPromotionRepeats = async (params: {
    page?: number;
    limit?: number;
    search?: string;
} = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return await request<PromotionRepeatsResponse>(
        `/v3/admin/promotions/repeats${suffix}`
    );
};

export const deletePromotionRepeat = async (repeatId: string) => {
    return await request<void>(
        `/v3/admin/promotions/repeats/${repeatId}`,
        { method: "DELETE" }
    );
};

export const unassignLearnerPromotion = async (userId: string) => {
    return await request<void>(
        `/v3/admin/promotions/learners/${userId}/promotion`,
        { method: "DELETE" }
    );
};

export type { LearnerRow, Promotion, PromotionInput, PromotionRepeatRow };
